import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, CheckCircle, XCircle, Star, MessageSquare, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { getAdminReviews, createReview, updateReview, deleteReview } from '../../services/api';
import { toast } from 'sonner';

const ReviewsAdmin = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    customer_name: '', company: '', designation: '', review_text: '', rating: 5, is_approved: true
  });

  const fetchData = async () => {
    try {
      const status = filter === 'all' ? null : filter;
      const res = await getAdminReviews(status);
      setReviews(res.data);
    } catch { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ customer_name: '', company: '', designation: '', review_text: '', rating: 5, is_approved: true });
    setDialogOpen(true);
  };

  const openEdit = (review) => {
    setEditing(review);
    setForm({
      customer_name: review.customer_name || '',
      company: review.company || '',
      designation: review.designation || '',
      review_text: review.review_text || '',
      rating: review.rating || 5,
      is_approved: review.is_approved
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateReview(editing.id, form);
        toast.success('Review updated');
      } else {
        await createReview(form);
        toast.success('Review added');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleApprove = async (review) => {
    try {
      await updateReview(review.id, { is_approved: !review.is_approved });
      toast.success(review.is_approved ? 'Review hidden' : 'Review approved');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await deleteReview(id);
      toast.success('Review deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const pending = reviews.filter(r => !r.is_approved).length;
  const approved = reviews.filter(r => r.is_approved).length;

  const StarRating = ({ rating, onChange, interactive = false }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onChange(i)}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
        >
          <Star size={interactive ? 22 : 16} className={i <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} />
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reviews-admin">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Reviews</h1>
          <p className="text-sm text-gray-500">
            {approved} approved, {pending} pending approval
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#2E3A6F]" data-testid="add-review-btn">
          <Plus size={16} className="mr-2" /> Add Review
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { key: 'all', label: 'All', count: reviews.length },
          { key: 'approved', label: 'Approved', count: approved },
          { key: 'pending', label: 'Pending', count: pending }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-[#2E3A6F] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-6 h-28" /></Card>)}</div>
      ) : reviews.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center text-gray-500">
            <MessageSquare className="mx-auto mb-3 text-gray-300" size={40} />
            <p>No reviews yet. Add one manually or wait for customer submissions.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id} className={`transition-shadow hover:shadow-md ${!review.is_approved ? 'border-l-4 border-l-yellow-400' : ''}`} data-testid={`review-card-${review.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#2E3A6F]/10 flex items-center justify-center flex-shrink-0">
                        <User size={18} className="text-[#2E3A6F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{review.customer_name}</h3>
                        <p className="text-xs text-gray-500">
                          {review.designation && `${review.designation}, `}{review.company}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-auto sm:ml-0">
                        <StarRating rating={review.rating} />
                        {review.source === 'customer' && (
                          <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">Customer</span>
                        )}
                        {!review.is_approved && (
                          <span className="text-[10px] px-2 py-0.5 bg-yellow-50 text-yellow-700 rounded-full font-medium">Pending</span>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed pl-[52px]">"{review.review_text}"</p>
                  </div>
                  <div className="flex gap-2 sm:flex-col">
                    <Button
                      size="sm"
                      variant={review.is_approved ? 'outline' : 'default'}
                      className={review.is_approved ? 'text-yellow-600 hover:bg-yellow-50' : 'bg-green-600 hover:bg-green-700 text-white'}
                      onClick={() => handleApprove(review)}
                      data-testid={`approve-btn-${review.id}`}
                    >
                      {review.is_approved ? <><XCircle size={14} className="mr-1" /> Hide</> : <><CheckCircle size={14} className="mr-1" /> Approve</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(review)}>
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(review.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Review</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Customer Name *</Label>
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required placeholder="e.g. Rajesh Kumar" data-testid="review-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Company</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="e.g. Tata Motors" />
              </div>
              <div>
                <Label>Designation</Label>
                <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g. Plant Manager" />
              </div>
            </div>
            <div>
              <Label>Review Text *</Label>
              <Textarea value={form.review_text} onChange={(e) => setForm({ ...form, review_text: e.target.value })} required rows={3} placeholder="Customer's review about your products/services..." data-testid="review-text-input" />
            </div>
            <div>
              <Label>Rating</Label>
              <StarRating rating={form.rating} onChange={(r) => setForm({ ...form, rating: r })} interactive />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_approved} onCheckedChange={(v) => setForm({ ...form, is_approved: v })} data-testid="review-approved-toggle" />
              <Label>Approved (visible on website)</Label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="review-submit-btn">{editing ? 'Update' : 'Add'} Review</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewsAdmin;
